import { useMemo } from 'react';
import { normalize } from 'lib/utils';
import usePDPContext from 'components/consumer/PDP/state/context/usePDPContext';
import useProductBasicDetails from '../useProductBasicDetails';
import useProductBoxDimensions from '../useProductBoxDimensions';
import getConfigurationOptionValueIds from './getConfigurationOptionValueIds';
const useProductAttributes = (id, slug, skip = false) => {
  const pdpProductOptions = usePDPContext()?.productOptions;
  const hasPdpOptionsContext = Boolean(pdpProductOptions);
  const selectedOptionsExtended = pdpProductOptions?.data?.selectedOptionsExtended;
  const configurationOptionValueIds = useMemo(() => hasPdpOptionsContext ? getConfigurationOptionValueIds(selectedOptionsExtended) : [], [hasPdpOptionsContext, selectedOptionsExtended]);
  const {
    loading,
    error,
    data
  } = useProductBasicDetails(id, slug, {}, {
    skip
  });
  const {
    error: boxDimensionsError,
    box_dimensions
  } = useProductBoxDimensions(id, slug, {
    configurationOptionValueIds,
    skip: skip || !hasPdpOptionsContext
  });
  const attributes = useMemo(() => !skip && data?.attributes ? normalize({
    data: data.attributes,
    key: 'code'
  }).data : null, [data?.attributes, skip]);

  // description
  const description = useMemo(() => !skip && attributes?.description?.value ? attributes.description.value : null, [attributes?.description?.value, skip]);
  const boxDimensions = useMemo(() => {
    if (skip || !box_dimensions) return [];
    return box_dimensions.reduce((accumulator, box) => {
      // Backbone stores shipping length on package_width and width on package_depth.
      const boxSizeDimensions = [box?.package_width != null ? `${box.package_width}"l` : '', box?.package_depth != null ? `${box.package_depth}"w` : '', box?.package_height != null ? `${box.package_height}"h` : ''].filter(Boolean).join(' x ');
      const boxWeight = box?.package_weight != null ? `(${box.package_weight} lbs)` : '';
      const boxDimensionsAndWeight = [boxSizeDimensions, boxWeight].filter(Boolean).join(' | ');
      if (boxDimensionsAndWeight.trim() !== '') {
        accumulator.push({
          label: `Box ${accumulator.length + 1}`,
          value: boxDimensionsAndWeight
        });
      }
      return accumulator;
    }, []);
  }, [box_dimensions, skip]);

  // dimension details
  const dimensions = useMemo(() => {
    if (!skip) {
      const sizeGeneralArr = [];
      const sizeSeatArr = [];
      const sizeArmArr = [];
      if (attributes?.width?.value) sizeGeneralArr.push(`${attributes.width.value}"w`);
      if (attributes?.depth?.value) sizeGeneralArr.push(` x ${attributes.depth.value}"d`);
      if (attributes?.height?.value) sizeGeneralArr.push(` x ${attributes.height.value}"h`);
      const sizeGeneral = sizeGeneralArr.join('  ');
      if (attributes?.seat_width?.value) sizeSeatArr.push(`${attributes.seat_width.value}"w`);
      if (attributes?.seat_depth?.value) sizeSeatArr.push(` x ${attributes.seat_depth.value}"d`);
      if (attributes?.seat_height?.value) sizeSeatArr.push(` x ${attributes.seat_height.value}"h`);
      const sizeSeat = sizeSeatArr.join('   ');
      if (attributes?.arm_width?.value) sizeArmArr.push(`${attributes.arm_width.value}"`);
      if (attributes?.arm_height?.value) sizeArmArr.push(` x ${attributes.arm_height.value}"`);
      const sizeArm = sizeArmArr.join('   ');
      const additionalSizesArr = attributes?.other_dimensions?.value ? JSON.parse(attributes.other_dimensions.value) : [];
      const weight = parseInt(attributes?.weight?.value || '', 10) ? `${attributes.weight.value} lbs` : '';
      const result = [sizeGeneral && {
        label: 'Overall',
        value: sizeGeneral
      }, sizeSeat && {
        label: 'Seating',
        value: sizeSeat
      }, sizeArm && {
        label: 'Arm Height',
        value: sizeArm
      }, ...(additionalSizesArr?.length ? additionalSizesArr.map(size => ({
        label: size.key,
        value: size.value
      })) : []), weight && {
        label: 'Weight',
        value: weight
      }].filter(Boolean);
      return result;
    }
    return [];
  }, [attributes?.arm_height?.value, attributes?.arm_width?.value, attributes?.depth?.value, attributes?.height?.value, attributes?.other_dimensions?.value, attributes?.seat_depth?.value, attributes?.seat_height?.value, attributes?.seat_width?.value, attributes?.weight?.value, attributes?.width?.value, skip]);

  // set product pdf
  const productPDF = useMemo(() => !skip && attributes?.product_pdf?.value ? attributes.product_pdf.value : null, [attributes?.product_pdf?.value, skip]);

  // set assemby details pdf
  const assemblyDetailsPDF = useMemo(() => !skip && attributes?.product_flyer?.value ? attributes.product_flyer.value : null, [attributes?.product_flyer?.value, skip]);

  // construction details
  const constructionDetails = useMemo(() => {
    const constructionDetailsArr = [];
    if (!skip && attributes?.construction_details?.value) {
      const arr = attributes.construction_details.value.trim().replace(/(\r\n|\n|\r)/gm, '').split('<br>');
      for (const item of arr) {
        if (item) {
          const itemDef = item.split(':');
          if (itemDef?.length) {
            const constructionValue = {};
            if (itemDef.length < 2) {
              constructionValue.label = '';
              constructionValue.description = itemDef[0].trim();
            } else if (itemDef.length === 2) {
              constructionValue.label = itemDef[0].trim();
              constructionValue.description = itemDef[1].trim();
            }
            constructionDetailsArr.push(constructionValue);
          }
        }
      }
    }
    return constructionDetailsArr;
  }, [attributes?.construction_details?.value, skip]);

  // product care
  const productCareDetails = useMemo(() => {
    const productCareArr = [];
    if (!skip && attributes?.product_care?.value) {
      const arr_0 = attributes.product_care.value.trim().replace(/(\r\n|\n|\r|")/gm, '').split('<br>');
      for (const item_0 of arr_0) {
        if (item_0) {
          const itemDef_0 = item_0.split(':');
          if (Array.isArray(itemDef_0) && itemDef_0?.length > 1) {
            productCareArr.push(itemDef_0[1].trim());
          }
        }
      }
    }
    return productCareArr;
  }, [attributes?.product_care?.value, skip]);

  // setting seo attributes
  const SEOAttributes = useMemo(() => {
    if (!skip) {
      return {
        meta_description: attributes?.meta_description?.value ?? null,
        meta_keywords: attributes?.meta_keywords?.value ?? null,
        canonicalURL: `https://joybird.com${attributes?.url_key?.value}` ?? null
      };
    }
    return {};
  }, [attributes?.meta_description?.value, attributes?.meta_keywords?.value, attributes?.url_key?.value, skip]);
  return {
    loading,
    error: error || boxDimensionsError,
    data: {
      id: data?.id,
      description,
      SEOAttributes,
      dimensions,
      productPDF,
      constructionDetails,
      productCareDetails,
      custom_properties: data?.custom_properties,
      additional_attributes: data?.additional_attributes,
      assemblyDetailsPDF,
      boxDimensions
    }
  };
};
export default useProductAttributes;